import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { FC } from "react";
import { useTranslation } from "react-i18next";

export const WorkingMethodPage: FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));
  const language = i18n.language;

  const diagrams = [
    {
      title: t("Traditional self-management"),
      src: `assets/workmethod/classiclooping_${language}.svg`,
    },
    {
      title: t("Self-management with Opensource.clinic"),
      src: `assets/workmethod/osclooping_${language}.svg`,
    },
  ];

  const helpPoints = [
    t("workmethod.cloudservice_only"),
    t("workmethod.how_we_help"),
    t("workmethod.how_we_help_detail"),
  ];

  return (
    <Box sx={{ backgroundColor: "background.paper", minHeight: "100vh" }}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          pt: { xs: 10, md: 14 },
          pb: { xs: 3, md: 4 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 680 }}>
            <Typography
              variant="overline"
              sx={{
                fontWeight: 700,
                letterSpacing: "0.14em",
                color: "primary.main",
                mb: 1.5,
                display: "block",
              }}
            >
              {t("Our Work Method")}
            </Typography>
            <Typography
              component="h1"
              variant="h1"
              sx={{
                fontWeight: 700,
                letterSpacing: "-0.03em",
                lineHeight: 1.05,
                fontSize: smDown ? "2.75rem" : "4rem",
              }}
            >
              {t("Our Mission")}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mt: 2,
                maxWidth: 600,
                fontSize: smDown ? "1rem" : "1.125rem",
                lineHeight: 1.6,
                color: "text.secondary",
                fontWeight: 500,
              }}
            >
              {t(
                "Empowering diabetes management through open-source loop solutions."
              )}
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container
        maxWidth="lg"
        sx={{ pt: { xs: 3, md: 4 }, pb: { xs: 6, md: 8 } }}
      >
        <Box sx={{ maxWidth: 720, mb: { xs: 5, md: 7 } }}>
          <Typography
            component="h2"
            variant="h3"
            sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 1.5 }}
          >
            {t("Why Opensource.clinic?")}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "text.secondary", lineHeight: 1.7, mb: 2.5 }}
          >
            {t("workmethod.mission_statement")}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "text.secondary", lineHeight: 1.7 }}
          >
            {t("workmethod.team_description")}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gap: { xs: 4, md: 6 },
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          }}
        >
          {diagrams.map((diagram) => (
            <Box key={diagram.src}>
              <Typography
                component="h2"
                variant="h6"
                sx={{ fontWeight: 600, mb: 2 }}
              >
                {diagram.title}
              </Typography>
              <Box
                component="img"
                src={diagram.src}
                alt={diagram.title}
                sx={{
                  width: "100%",
                  maxWidth: 480,
                  display: "block",
                }}
              />
            </Box>
          ))}
        </Box>
      </Container>

      <Box
        sx={{
          borderTop: 1,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(99, 102, 241, 0.08)"
              : "#F7F9FC",
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            component="h2"
            variant="h3"
            sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 1 }}
          >
            {t("What we do")}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gap: { xs: 3, md: 4 },
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              mt: { xs: 4, md: 5 },
            }}
          >
            <Typography
              component="div"
              variant="body1"
              sx={{ color: "text.secondary", lineHeight: 1.7 }}
              dangerouslySetInnerHTML={{ __html: t("workmethod.what_we_do") }}
            />
            <Typography
              component="div"
              variant="body1"
              sx={{ color: "text.secondary", lineHeight: 1.7 }}
              dangerouslySetInnerHTML={{
                __html: t("workmethod.what_we_do_detail"),
              }}
            />
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Box sx={{ maxWidth: 720, mb: { xs: 5, md: 7 } }}>
          <Typography
            component="h2"
            variant="h3"
            sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 1.5 }}
          >
            {t("How we help")}
          </Typography>
          <Stack spacing={3}>
            {helpPoints.map((point) => (
              <Box
                key={point}
                sx={{
                  borderLeft: 3,
                  borderColor: "primary.main",
                  pl: 2.5,
                  py: 0.5,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ color: "text.secondary", lineHeight: 1.7 }}
                >
                  {point}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        <Box
          sx={{
            display: "grid",
            gap: { xs: 4, md: 6 },
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            alignItems: "start",
          }}
        >
          <Box>
            <Typography
              component="h2"
              variant="h3"
              sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 1.5 }}
            >
              {t("How we stay in touch")}
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "text.secondary", lineHeight: 1.7 }}
            >
              {t("workmethod.how_we_stay_in_touch")}
            </Typography>
          </Box>
          <Box>
            <Typography
              component="h2"
              variant="h3"
              sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 1.5 }}
            >
              {t("What we don't do")}
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "text.secondary", lineHeight: 1.7 }}
            >
              {t("workmethod.what_we_dont_do")}
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};
